import { get } from 'lodash/fp';
import { createSelector } from 'reselect';
import { flow } from 'fp-ts/function';
import { array } from 'fp-ts';
import { createAction, createAsyncActionTypes } from '@mews/framework';
import { getStringProp } from '@mews/fp';
import { ApplicationState, CommanderThunkAction } from '@mews-commander/types';
import { Voucher, GetVoucherParameters } from '@mews-commander/data-access';
import { createFetchAction } from '@mews-commander/utils';
import moduleSelector from '../selector';
import prefix from '../prefix';
import { setCompanies } from './companies';
import { setRates } from './rates';
import { createBaseEntitySelectors, createEntitiesReducer } from './utils';

export const NAME = 'vouchers';

const VOUCHERS_LOADED = prefix('VOUCHERS_LOADED');
const VOUCHERS_FETCH = prefix('VOUCHERS_FETCH');
const VOUCHERS_DELETE = prefix('VOUCHERS_DELETE');
const [, VOUCHERS_DELETE_SUCCEEDED] = createAsyncActionTypes(VOUCHERS_DELETE);

const VOUCHERS_UPDATE = prefix('VOUCHERS_UPDATE');
const [, VOUCHERS_UPDATE_SUCCEEDED] = createAsyncActionTypes(VOUCHERS_UPDATE);

const VOUCHERS_BULK_ADD = prefix('VOUCHERS_BULK_ADD');
const [, VOUCHERS_BULK_ADD_SUCCEEDED] = createAsyncActionTypes(VOUCHERS_BULK_ADD);

type FetchParameters = Omit<GetVoucherParameters, 'accessToken' | 'client'>;
const fetch =
    (params: FetchParameters): CommanderThunkAction =>
    async (dispatch, getState, { repositories }) => {
        const promise = repositories.vouchers.getAll(params).then(({ vouchers, companies, rates }) => {
            if (vouchers && array.isNonEmpty(vouchers)) {
                dispatch(setVouchers(vouchers));
            }
            if (companies && array.isNonEmpty(companies)) {
                dispatch(setCompanies(companies));
            }

            if (rates && array.isNonEmpty(rates)) {
                // @ts-expect-error $TSFixMeError: Expected 2 arguments, but got 1.
                dispatch(setRates(rates));
            }
        });
        return dispatch(createFetchAction(VOUCHERS_FETCH, promise));
    };

export const fetchEnterpriseVouchers = ({ enterpriseIds, extent }: Pick<FetchParameters, 'enterpriseIds' | 'extent'>) =>
    fetch({ enterpriseIds, extent });

export const fetchServiceVouchers = ({
    enterpriseIds,
    serviceIds,
    extent,
    type,
}: Pick<FetchParameters, 'enterpriseIds' | 'serviceIds' | 'extent' | 'type'>) =>
    fetch({ enterpriseIds, serviceIds, extent, type });

export const setVouchers = (vouchers: Array<Voucher>) => createAction(VOUCHERS_LOADED, { entities: vouchers });

export const vouchersReducer = createEntitiesReducer({
    FETCH_SUCCESS: VOUCHERS_LOADED,
    DELETE_SUCCESS: VOUCHERS_DELETE_SUCCEEDED,
    UPDATE_SUCCESS: VOUCHERS_UPDATE_SUCCEEDED,
    ADD_SUCCESS: VOUCHERS_BULK_ADD_SUCCEEDED,
});
export const [getVouchers, getVouchersByIds, getVoucherByOwnerId, getVoucher] = createBaseEntitySelectors<
    ApplicationState,
    Voucher,
    typeof NAME
>(moduleSelector, NAME);

export const getVoucherName = flow(getVoucher, get('name'));

export const getServiceVouchers = createSelector(
    getVouchers,
    getStringProp('serviceId'),
    (vouchers: Record<string, Voucher>, serviceId: string) =>
        array.filter((voucher: Voucher) => voucher.serviceId === serviceId)(Object.values(vouchers))
);

export const getServicePublicVouchers = flow(
    getServiceVouchers,
    array.filter((voucher: Voucher) => voucher.companyId === null && voucher.travelAgencyId === null)
);
